import { createCanvas, destroyCanvas, drawImage } from "@/libs/canvas"
import type { FramePixels } from "@/libs/cv"
import type { Rect } from "@/libs/geometry/types"
import { mapValues } from "@/libs/lodash"

export type ImageSource = TexImageSource & CanvasImageSource

/** @return Display width and height of the given image source */
export const getImageSourceDimensions = (source: ImageSource) => {
  if (isVideoFrame(source)) return { width: source.displayWidth, height: source.displayHeight }
  if (isHTMLVideoElement(source)) return { width: source.videoWidth, height: source.videoHeight }
  if (isHTMLImageElement(source))
    return { width: source.naturalWidth, height: source.naturalHeight }

  return { width: source.width, height: source.height }
}

export const getImageDataFromSource = (source: ImageSource): Promise<ImageData> => {
  const canvas = createCanvas()

  drawImage(canvas, source)

  const ctx = canvas.getContext("2d") as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null
  if (!ctx) throw new Error("Failed to get canvas 2D context")

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  destroyCanvas(canvas)

  return Promise.resolve(imageData)
}

export const imageBitmapToImageData = (imageBitmap: ImageBitmap): Promise<ImageData> =>
  getImageDataFromSource(imageBitmap)

export const isVideoFrame = (obj: any): obj is VideoFrame =>
  typeof VideoFrame !== "undefined" && obj instanceof VideoFrame
export const isHTMLVideoElement = (obj: any): obj is HTMLVideoElement => obj?.nodeName === "VIDEO"
export const isHTMLImageElement = (obj: any): obj is HTMLImageElement => obj?.nodeName === "IMAGE"

/** Returns coordinates of visible rect of the original image source for the given object fit mode */
export const getVisibleRect = (
  image: HTMLCanvasElement | HTMLVideoElement,
  objectFit: "cover" | "contain" = "cover",
) => {
  const { width, height } = getImageSourceDimensions(image)

  if (objectFit === "cover") {
    const { clientWidth, clientHeight } = image

    const scale = Math.max(clientWidth / width, clientHeight / height)
    const px = (width - clientWidth / scale) / 2
    const py = (height - clientHeight / scale) / 2

    return {
      x: Math.max(0, Math.round(px)),
      y: Math.max(0, Math.round(py)),
      width: Math.min(Math.round(width - 2 * px), width),
      height: Math.min(Math.round(height - 2 * py), height),
    }
  }

  return { x: 0, y: 0, width, height }
}

/**
 * Fits the given width and height into the given resolution.
 * E.g given 480p resolution, the function will fit the smallest dimension into 480px
 * and adjust the second one accordingly while keeping the aspect ratio.
 */
export const fit = ({ width, height }: { width: number; height: number }, resolution: number) => {
  const scale = resolution / Math.min(width, height)

  return mapValues({ width, height }, (size) => Math.floor(size * scale))
}

/**
 * Fits the given width and height into the given max area.
 * E.g. given 1 megapixel max area, the function will scale down the width
 * and height so that their product is less or equal to 1 megapixel.
 */
export const fitArea = (size: { width: number; height: number }, maxArea: number) => {
  const area = size.width * size.height
  if (area <= maxArea) return size

  const scale = Math.sqrt(maxArea) / Math.sqrt(area)

  return { width: Math.floor(size.width * scale), height: Math.floor(size.height * scale) }
}

export const fitWithinBounds = (
  size: { width: number; height: number },
  bounds: { width: number; height: number },
) => {
  if (size.width <= bounds.width && size.height <= bounds.height) return size

  const scale = Math.min(bounds.width / size.width, bounds.height / size.height)

  return {
    width: Math.floor(size.width * scale),
    height: Math.floor(size.height * scale),
  }
}

export const createImage = async (src: string): Promise<HTMLImageElement> => {
  const img = new Image()

  await new Promise<void>((resolve, reject) => {
    img.addEventListener("load", () => resolve(), { once: true })
    img.addEventListener("error", () => reject(new Error("Failed to load the image file")), {
      once: true,
    })
    img.src = src
  })

  return img
}
export const destroyImage = (img: HTMLImageElement) => {
  URL.revokeObjectURL(img.src)
  img.src = ""
}

/**
 * Maximum canvas size supported by most browsers.
 * Mobile browsers typically have stricter limits.
 */
export const MAX_CANVAS_SIZE = 4096

/**
 * Checks if image dimensions exceed the maximum canvas size limits.
 * @param width Image width in pixels
 * @param height Image height in pixels
 * @returns true if image exceeds size limits
 */
export const exceedsCanvasSizeLimit = (width: number, height: number): boolean => {
  return width > MAX_CANVAS_SIZE || height > MAX_CANVAS_SIZE
}

export const blobToImage = async (
  blob: Blob,
  {
    maxSize = 0,
  }: {
    /**
     * The upper limit for the returned image width and height.
     * If at least on of the input dimensions is larger than the given `maxSize`,
     * it will be downscaled to fit into the `maxSize` preserving the input's aspect ratio.
     */
    maxSize?: number
  } = {},
): Promise<HTMLImageElement> => {
  // we have to use the Image API since it handles rotation of iOS photos compared for example to createImageBitmap
  const img = await createImage(URL.createObjectURL(blob))

  if (!maxSize || maxSize < 1 || [img.width, img.height].every((dim) => dim <= maxSize)) return img

  const scale = maxSize / Math.max(img.width, img.height)
  const image = await imageBitmapToImage(
    await createImageBitmap(img, {
      resizeWidth: img.width * scale,
      resizeHeight: img.height * scale,
      resizeQuality: "high",
    }),
  )

  destroyImage(img)

  return image
}

export const imageBitmapToImage = async (
  imageBitmap: ImageBitmap,
  { rect }: { rect?: Rect } = {},
): Promise<HTMLImageElement> => {
  const canvas = createCanvas()

  drawImage(canvas, imageBitmap, { rect })

  const blob = await canvas.convertToBlob({ type: "image/png", quality: 1 })
  const img = await createImage(URL.createObjectURL(blob))

  destroyCanvas(canvas)
  imageBitmap.close()

  return img
}

export type ImageFormat = "jpeg" | "png" | "webp"

export const imageToBlob = async (
  image: HTMLImageElement,
  options?: { format?: ImageFormat; horizontalFlip?: boolean },
): Promise<Blob> => {
  const canvas = createCanvas()

  drawImage(canvas, image, { horizontalFlip: options?.horizontalFlip })

  try {
    return await canvas.convertToBlob({ type: `image/${options?.format ?? "jpeg"}`, quality: 1 })
  } finally {
    destroyCanvas(canvas)
  }
}

/**
 * Hack for converting large images to pixels without webgl2 buffer issues.
 * Is used in processing 'upload photo' image-source.
 * @param source Image source to convert to pixels.
 * @returns Pixels of the image source.
 */
export const imageBitmapToPixels = (source: ImageBitmap): FramePixels => {
  const canvas = createCanvas(source.width, source.height)
  const pixels = new Uint8Array(source.width * source.height * 4)
  const ctx = canvas.getContext("2d") as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
  if (!ctx) throw new Error("Failed to get canvas 2D context")
  ctx.drawImage(source, 0, 0)
  const imageData = ctx.getImageData(0, 0, source.width, source.height)
  pixels.set(imageData.data)
  destroyCanvas(canvas)
  return { data: pixels, width: source.width, height: source.height, format: "RGBA" }
}
